import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X, Globe } from 'lucide-react';

const Header = ({ t, language, setLanguage }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);

  const handleScroll = useCallback(() => setIsScrolled(window.scrollY > 10), []);
  const handleResize = useCallback(() => { if (window.innerWidth >= 1024) setIsMobileMenuOpen(false); }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (window.innerWidth >= 1024) setIsMobileMenuOpen(false);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const languages = [
    { code: 'en', label: 'EN' },
    { code: 'es', label: 'ES' },
    { code: 'zh', label: '中文' }
  ];

  const navItems = [
    { id: 'hero', label: t.nav.overview },
    { id: 'development', label: t.nav.development },
    { id: 'location', label: t.nav.location },
    { id: 'amenities', label: t.nav.amenities },
    { id: 'nearshoring', label: t.nav.nearshoring },
    { id: 'contact', label: t.nav.contact }
  ];

  const scrollToSection = (id) => {
    if (id === 'hero') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const el = document.getElementById(id);
      if (el) {
        const offset = 64;
        const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: pos, behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header 
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
          isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-white'
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 lg:h-16">
            {/* Logo */}
            <button
              data-testid="header-logo"
              onClick={() => scrollToSection('hero')}
              className="flex items-center gap-2 hover:opacity-70 transition-opacity"
            >
              <img src="/images/logos/recurso_63.svg" alt="Hexaparks" className="h-5 sm:h-6 w-auto" />
              <div className="flex flex-col items-center leading-none">
                <img src="/images/logos/hexaparks_wordmark.svg" alt="Hexaparks" className="h-2.5 sm:h-3 w-auto" />
                <span className="text-[5px] sm:text-[6px] text-gray-500 tracking-[0.15em] uppercase mt-[1px]">{t.logo?.slogan}</span>
              </div>
            </button>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  data-testid={`nav-${item.id}`}
                  onClick={() => scrollToSection(item.id)}
                  className="px-3 py-2 text-xs text-gray-600 hover:text-gray-900 transition-colors uppercase tracking-wider"
                >
                  {item.label}
                </button>
              ))}

              {/* Desktop Language */}
              <div className="relative ml-2">
                <button
                  data-testid="desktop-lang-toggle"
                  onClick={() => setIsLangOpen(!isLangOpen)}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs text-gray-500 hover:text-gray-700"
                >
                  <Globe className="w-3.5 h-3.5" />
                  <span>{languages.find(l => l.code === language)?.label}</span>
                </button>
                {isLangOpen && (
                  <>
                    <div className="fixed inset-0" onClick={() => setIsLangOpen(false)} />
                    <div className="absolute right-0 mt-1 w-24 bg-white border border-gray-200 shadow-lg py-1">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          data-testid={`lang-option-${lang.code}`}
                          onClick={() => { setLanguage(lang.code); setIsLangOpen(false); }}
                          className={`w-full px-3 py-2 text-xs text-left ${
                            language === lang.code ? 'text-gray-900 bg-gray-50' : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {lang.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 -mr-2 text-gray-700"
              aria-label="Menu"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" style={{ top: '56px' }}>
          <div className="absolute inset-0 bg-black/20" onClick={() => setIsMobileMenuOpen(false)} />
          <nav 
            data-testid="mobile-menu"
            className="absolute top-0 left-0 right-0 bg-white border-b border-gray-200 shadow-lg max-h-[calc(100vh-56px)] overflow-y-auto"
          >
            <div className="py-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  data-testid={`mobile-nav-${item.id}`}
                  onClick={() => scrollToSection(item.id)}
                  className="block w-full text-left px-5 py-3 text-sm text-gray-700 hover:bg-gray-50"
                >
                  {item.label}
                </button>
              ))}
              
              {/* Mobile Language */}
              <div className="px-5 py-3 border-t border-gray-100 mt-2">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Language</p>
                <div className="flex gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      data-testid={`mobile-lang-${lang.code}`}
                      onClick={() => { setLanguage(lang.code); setIsMobileMenuOpen(false); }}
                      className={`px-3 py-1.5 text-xs ${
                        language === lang.code 
                          ? 'bg-gray-900 text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;
